import { apiErrorSchema } from '@posilka/shared';

/** Sukut: bir origin'dagi `/api/v1` (Vite proxy / Next rewrites / Caddy). Ilova `configureApi` bilan o'zgartiradi. */
let API_URL = '/api/v1';

/**
 * Framework'ga bog'liq emas (Vite `import.meta.env`, Next `process.env` — ilova o'zi uzatadi).
 * Serverda (Next server komponentlar) to'liq URL kerak: `configureApi({ baseUrl: 'http://api:4000/api/v1' })`.
 */
export function configureApi(opts: { baseUrl?: string }) {
  if (opts.baseUrl) API_URL = opts.baseUrl.replace(/\/$/, '');
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Server umuman javob bermadi: tarmoq uzilgan, proxy/tunnel o'lik (`NETWORK_ERROR`) yoki 15 s ichida
 * javob kelmadi (`TIMEOUT`). ApiError emas — retry qoidalari (`status < 500` → qayta urinmaslik) unga tegmaydi.
 */
export class NetworkError extends Error {
  constructor(
    readonly code: 'NETWORK_ERROR' | 'TIMEOUT',
    options?: ErrorOptions,
  ) {
    super(code, options);
  }
}

/** Foydalanuvchiga ko'rsatiladigan xato kodi (`errors.<CODE>` tarjimasi uchun). */
export const errorCode = (error: unknown): string | null =>
  error instanceof ApiError || error instanceof NetworkError ? error.code : null;

let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

/** So'rov osilib qolsa (tarmoq, proxy) UI abadiy kutmasin — xato holati va "Qayta urinish". */
const REQUEST_TIMEOUT_MS = 15_000;

/** `path` — `/api/v1` ga nisbatan (`/me`) yoki absolyut URL. */
export async function api<T>(path: string, { body, headers, ...init }: RequestOptions = {}) {
  const url = /^https?:\/\//.test(path) ? path : `${API_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: init.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (e) {
    // Chaqiruvchi o'zi bekor qilgan (TanStack Query cancel) — asl xato o'zgarmasin.
    if (init.signal?.aborted) throw e;
    const timeout = e instanceof DOMException && e.name === 'TimeoutError';
    throw new NetworkError(timeout ? 'TIMEOUT' : 'NETWORK_ERROR', { cause: e });
  }

  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);

  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(data);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new ApiError(res.status, code, message, details);
    }
    // JSON'siz 5xx — bizning API emas, oradagi proxy/tunnel javobi (API o'chiq yoki qayta ishga tushmoqda).
    throw new ApiError(
      res.status,
      res.status >= 500 ? 'SERVER_UNAVAILABLE' : 'HTTP_ERROR',
      res.statusText,
    );
  }

  return data as T;
}
